import type { AssessmentStatusDto, LessonDetailDto } from './types';
import { isLessonCompletedDto } from './lessonStepState';
function lessonsProgress(lessons: LessonDetailDto[]) {
    const total = lessons.length;
    const completed = lessons.filter(isLessonCompletedDto).length;
    const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
    return { completed, total, pct };
}

function assessmentProgress(assessmentId: string | undefined, status: AssessmentStatusDto | undefined) {
    if (!assessmentId?.trim()) {
        return { pct: 0, label: '0 / 0' };
    }
    const key = status?.e251_Result?.e251_ResultState?.key;
    if (key === 'teacher-approved') {
        return { pct: 100, label: '1 / 1' };
    }
    if (key === 'abandoned') {
        return { pct: 0, label: '0 / 1' };
    }
    if (status?.e251_Result) {
        return { pct: 66, label: '0 / 1' };
    }
    if (status?.e251_SharedAssessment) {
        return { pct: 33, label: '0 / 1' };
    }
    return { pct: 12, label: '0 / 1' };
}

interface UnitCardHeaderProgressBarsProps {
    lessons: LessonDetailDto[];
    assessmentId?: string;
    assessmentStatus?: AssessmentStatusDto;
    tone?: 'default' | 'chalkboard';
}

/** Thin side-by-side progress row for the in-progress unit card header only. */
export function UnitCardHeaderProgressBars({
    lessons,
    assessmentId,
    assessmentStatus,
}: UnitCardHeaderProgressBarsProps) {
    const m = lessonsProgress(lessons);
    const a = assessmentProgress(assessmentId, assessmentStatus);

    return (
        <div
            className="grid w-full grid-cols-2 gap-3 text-xs text-gray-600"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
        >
            <div className="min-w-0">
                <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-gray-500">Lessons completed</span>
                    <span className="shrink-0 tabular-nums text-gray-700">
                        {m.total === 0 ? '—' : `${m.completed} / ${m.total}`}
                    </span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-300/80">
                    <div
                        className="h-full rounded-full bg-blumine transition-[width] duration-300 ease-out"
                        style={{ width: `${m.pct}%` }}
                    />
                </div>
            </div>
            <div className="min-w-0">
                <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-gray-500">Assessments checked</span>
                    <span className="shrink-0 tabular-nums text-gray-700">{a.label}</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-300/80">
                    <div
                        className="h-full rounded-full bg-vibrant-green transition-[width] duration-300 ease-out"
                        style={{ width: `${a.pct}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
